// src/Components/dashboard/AIAdvisoryPage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell, RadarChart,
  Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from 'recharts';
import { Brain, Zap, Target, Lightbulb, TrendingUp, Users, CheckCircle, Clock } from 'lucide-react';
import { dashboardAPI } from '../../services/api';
import { createDashboardRealtimeClient } from '../../services/analyticsRealtime';
import { useTimeRange } from '../layout/DashboardLayout';
import {
  fmt, Skeleton, Card, PageHeader, EmptyState,
  RefreshBtn, ExportBtn, CHART_COLORS,
} from '../dashboard/design-system';

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-gray-900 border border-white/10 rounded-xl px-3 py-2.5 shadow-xl text-xs">
      <p className="text-white/50 mb-1.5">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.fill }} />
          <span className="text-white/70">{p.name}:</span>
          <span className="text-white font-semibold">{fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

// ── Score ring ────────────────────────────────────────────────────────────────
const ScoreRing = ({ value, max = 1, label, color }) => {
  const pct = Math.min((value / max) * 100, 100);
  const r = 28, circ = 2 * Math.PI * r;
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative w-16 h-16">
        <svg className="w-16 h-16 -rotate-90" viewBox="0 0 72 72">
          <circle cx="36" cy="36" r={r} fill="none" stroke="#f1f5f9" strokeWidth="6" />
          <circle cx="36" cy="36" r={r} fill="none" stroke={color} strokeWidth="6"
            strokeDasharray={circ} strokeDashoffset={circ * (1 - pct / 100)}
            strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.6s ease' }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-sm font-bold text-gray-800 dark:text-white">{Math.round(pct)}%</span>
        </div>
      </div>
      <p className="text-xs text-gray-500 text-center">{label}</p>
    </div>
  );
};

// ── Metric block card ─────────────────────────────────────────────────────────
const MetricBlock = ({ icon: Icon, accent, title, metrics, loading }) => (
  <Card className="p-5">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${accent}18` }}>
        <Icon size={18} style={{ color: accent }} />
      </div>
      <h3 className="font-semibold text-gray-900 dark:text-white text-sm">{title}</h3>
    </div>
    {loading ? (
      <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
    ) : (
      <div className="grid grid-cols-2 gap-2">
        {metrics.map(({ label, value, highlight }) => (
          <div key={label} className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-3">
            <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">{label}</p>
            <p className={`text-lg font-bold mt-0.5 ${highlight ? 'text-red-600' : 'text-gray-900 dark:text-white'}`}>
              {value ?? '—'}
            </p>
          </div>
        ))}
      </div>
    )}
  </Card>
);

// ═══════════════════════════════════════════════════════════════════════════════
export default function AIAdvisoryPage() {
  const { timeRange } = useTimeRange();
  const clientRef = useRef(null);

  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [ai,          setAi]          = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);
  const [realtime,    setRealtime]    = useState(false);

  const applyAI = useCallback((snapshot) => {
    const data = snapshot?.data?.ai || snapshot?.ai || snapshot?.data || {};
    setAi(data);
    setLastUpdated(new Date());
  }, []);

  const fetchAI = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await dashboardAPI.getDashboardSnapshot(timeRange, 10);
      console.log('🤖 AI snapshot:', res?.data?.ai);
      if (res?.data?.ai) setAi(res.data.ai);
      else if (res?.data) setAi({});
      setLastUpdated(new Date());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [timeRange]);

  useEffect(() => { fetchAI(); }, [fetchAI]);

  useEffect(() => {
    const c = createDashboardRealtimeClient({
      range: timeRange,
      onSnapshot: p => { applyAI(p); setRealtime(true); },
      onUpdate:   p => applyAI(p),
      onError:    () => setRealtime(false),
    });
    clientRef.current = c;
    c.requestSnapshot();
    return () => { c.disconnect(); clientRef.current = null; };
  }, [applyAI, timeRange]);

  const exportCSV = () => {
    const rows = [
      ['Beetroot – AI Analytics', new Date().toLocaleString(), timeRange],
      [],
      ['Section', 'Metric', 'Value'],
      ['AI Advisor',    'Total Conversations',  ai.aiAdvisor?.totalConversations  ?? 0],
      ['AI Advisor',    'Total Insights',        ai.aiAdvisor?.totalInsights       ?? 0],
      ['AI Advisor',    'Unread Insights',        ai.aiAdvisor?.unreadInsights      ?? 0],
      ['AI Advisor',    'Avg Confidence',         ai.aiAdvisor?.avgConfidence       ?? 0],
      ['AI Prompts',    'Total Prompts',          ai.aiPrompts?.totalPrompts        ?? 0],
      ['Funding Match', 'Total Matches',          ai.fundingMatchmaking?.totalMatches ?? 0],
      ['Funding Match', 'Connected',              ai.fundingMatchmaking?.connectedMatches ?? 0],
      ['Smart Match',   'Total Matches',          ai.smartMatchmaking?.totalMatches ?? 0],
      ['Opportunity',   'Total Opportunities',    ai.opportunityHub?.totalOpportunities ?? 0],
      ['AI Content',    'Total Recommendations',  ai.aiContent?.totalRecommendations ?? 0],
    ].map(r => r.join(',')).join('\n');
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([rows], { type: 'text/csv' })),
      download: `ai-analytics-${timeRange}-${new Date().toISOString().split('T')[0]}.csv`,
    });
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  // Category breakdown for bar chart
  const promptCategories = Object.entries(ai.aiPrompts?.promptsByCategory || {})
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);

  const advisorCategories = Object.entries(ai.aiAdvisor?.categories || {})
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);

  // Radar: AI health overview
  const radarData = [
    { subject: 'Advisor', value: Math.min((ai.aiAdvisor?.totalConversations ?? 0) / 100, 100) },
    { subject: 'Prompts', value: Math.min((ai.aiPrompts?.totalPrompts ?? 0) / 100, 100) },
    { subject: 'Funding', value: Math.min((ai.fundingMatchmaking?.totalMatches ?? 0) / 50, 100) },
    { subject: 'Smart Match', value: Math.min((ai.smartMatchmaking?.totalMatches ?? 0) / 50, 100) },
    { subject: 'Opportunities', value: Math.min((ai.opportunityHub?.totalOpportunities ?? 0) / 50, 100) },
    { subject: 'Content AI', value: Math.min((ai.aiContent?.totalRecommendations ?? 0) / 50, 100) },
  ];

  const hasData = Object.keys(ai).length > 0 && !loading;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-6 space-y-6">

      <PageHeader title="AI Advisory" sub={`AI feature usage & performance · ${timeRange}`}
        icon={Brain} lastUpdated={lastUpdated} realtime={realtime}>
        <RefreshBtn onClick={fetchAI} loading={loading} />
        <ExportBtn onClick={exportCSV} />
      </PageHeader>

      {error && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 rounded-xl px-4 py-3 text-sm">
          {error} — <button onClick={fetchAI} className="underline font-medium">retry</button>
        </div>
      )}

      {/* ── Traction KPIs — top strip ─────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: Brain,       accent: '#C8102E', title: 'AI Advisor Prompts',  value: fmt(ai.aiPrompts?.totalPrompts ?? ai.aiAdvisor?.totalConversations), sub: 'Questions asked'      },
          { icon: Target,      accent: '#0EA5E9', title: 'Match Applications',  value: fmt((ai.fundingMatchmaking?.totalMatches ?? 0) + (ai.smartMatchmaking?.totalMatches ?? 0)), sub: 'Business match requests' },
          { icon: Zap,         accent: '#F59E0B', title: 'AI Credits Used',     value: fmt(ai.aiAdvisor?.totalConversations ?? 0), sub: 'Proxy: conversations' },
          { icon: Lightbulb,   accent: '#10B981', title: 'AI Insights',         value: fmt(ai.aiAdvisor?.totalInsights),         sub: `${fmt(ai.aiAdvisor?.unreadInsights)} unread`  },
        ].map(item => (
          <Card key={item.title} className="p-5">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: `${item.accent}18` }}>
              <item.icon size={20} style={{ color: item.accent }} />
            </div>
            {loading ? <Skeleton className="h-8 w-24 mb-1" /> : (
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{item.value ?? '—'}</p>
            )}
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mt-0.5">{item.title}</p>
            <p className="text-xs text-gray-400">{item.sub}</p>
          </Card>
        ))}
      </div>

      {/* ── Confidence + score rings ──────────────────────────────────────── */}
      {hasData && (
        <Card className="p-6">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-5">AI Performance Scores</h3>
          <div className="flex flex-wrap gap-8 justify-center md:justify-start">
            <ScoreRing
              value={ai.aiAdvisor?.avgConfidence ?? 0} max={1}
              label="Advisor Confidence" color="#C8102E"
            />
            <ScoreRing
              value={ai.fundingMatchmaking?.avgMatchScore ?? 0} max={1}
              label="Funding Match Score" color="#0EA5E9"
            />
            <ScoreRing
              value={ai.smartMatchmaking?.avgMatchScore ?? 0} max={1}
              label="Smart Match Score" color="#10B981"
            />
            <ScoreRing
              value={ai.opportunityHub?.avgMatchScore ?? 0} max={1}
              label="Opportunity Score" color="#F59E0B"
            />
            <ScoreRing
              value={ai.aiContent?.avgRelevanceScore ?? 0} max={1}
              label="Content Relevance" color="#7C3AED"
            />
            <ScoreRing
              value={ai.aiContent?.viewedRecommendations ?? 0}
              max={Math.max(ai.aiContent?.totalRecommendations ?? 1, 1)}
              label="Recommendation CTR" color="#EC4899"
            />
          </div>
        </Card>
      )}

      {/* ── Metric blocks ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <MetricBlock icon={Brain} accent="#C8102E" title="AI Advisor" loading={loading}
          metrics={[
            { label: 'Conversations', value: fmt(ai.aiAdvisor?.totalConversations) },
            { label: 'Avg Confidence', value: ai.aiAdvisor?.avgConfidence != null ? `${(ai.aiAdvisor.avgConfidence * 100).toFixed(0)}%` : '—' },
            { label: 'Total Insights', value: fmt(ai.aiAdvisor?.totalInsights) },
            { label: 'Unread',         value: fmt(ai.aiAdvisor?.unreadInsights), highlight: true },
          ]}
        />
        <MetricBlock icon={Target} accent="#0EA5E9" title="Funding Matchmaking" loading={loading}
          metrics={[
            { label: 'Total Matches',   value: fmt(ai.fundingMatchmaking?.totalMatches) },
            { label: 'Connected',       value: fmt(ai.fundingMatchmaking?.connectedMatches), highlight: true },
            { label: 'New',             value: fmt(ai.fundingMatchmaking?.newMatches) },
            { label: 'Avg Match Score', value: ai.fundingMatchmaking?.avgMatchScore != null ? `${(ai.fundingMatchmaking.avgMatchScore * 100).toFixed(0)}%` : '—' },
          ]}
        />
        <MetricBlock icon={Users} accent="#10B981" title="Smart Matchmaking" loading={loading}
          metrics={[
            { label: 'Total Matches',   value: fmt(ai.smartMatchmaking?.totalMatches) },
            { label: 'Connected',       value: fmt(ai.smartMatchmaking?.connectedMatches), highlight: true },
            { label: 'New',             value: fmt(ai.smartMatchmaking?.newMatches) },
            { label: 'Avg Match Score', value: ai.smartMatchmaking?.avgMatchScore != null ? `${(ai.smartMatchmaking.avgMatchScore * 100).toFixed(0)}%` : '—' },
          ]}
        />
        <MetricBlock icon={TrendingUp} accent="#F59E0B" title="Opportunity Hub" loading={loading}
          metrics={[
            { label: 'Total',    value: fmt(ai.opportunityHub?.totalOpportunities) },
            { label: 'Applied',  value: fmt(ai.opportunityHub?.appliedOpportunities), highlight: true },
            { label: 'New',      value: fmt(ai.opportunityHub?.newOpportunities) },
            { label: 'Expired',  value: fmt(ai.opportunityHub?.expiredOpportunities) },
          ]}
        />
        <MetricBlock icon={Lightbulb} accent="#7C3AED" title="AI Content Recommendations" loading={loading}
          metrics={[
            { label: 'Total',    value: fmt(ai.aiContent?.totalRecommendations) },
            { label: 'Viewed',   value: fmt(ai.aiContent?.viewedRecommendations), highlight: true },
            { label: 'Pending',  value: fmt(ai.aiContent?.pendingRecommendations) },
            { label: 'Relevance', value: ai.aiContent?.avgRelevanceScore != null ? `${(ai.aiContent.avgRelevanceScore * 100).toFixed(0)}%` : '—' },
          ]}
        />
        <MetricBlock icon={CheckCircle} accent="#EC4899" title="AI Prompts" loading={loading}
          metrics={[
            { label: 'Total Prompts', value: fmt(ai.aiPrompts?.totalPrompts), highlight: true },
            { label: 'Recent (5)',    value: fmt(ai.aiPrompts?.recentPrompts?.length) },
            { label: 'Categories',   value: fmt(Object.keys(ai.aiPrompts?.promptsByCategory || {}).length) },
            { label: 'Top Category', value: Object.entries(ai.aiPrompts?.promptsByCategory || {}).sort((a, b) => b[1] - a[1])[0]?.[0] || '—' },
          ]}
        />
      </div>

      {/* ── Prompt category chart ─────────────────────────────────────────── */}
      {!loading && promptCategories.length > 0 && (
        <Card className="p-6">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-5">Prompt Categories</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={promptCategories} margin={{ top: 0, right: 0, left: -20, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} angle={-30} textAnchor="end" axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="value" name="Prompts" radius={[6, 6, 0, 0]}>
                {promptCategories.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* ── AI radar ─────────────────────────────────────────────────────── */}
      {hasData && (
        <Card className="p-6">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-5">AI Feature Activity Overview</h3>
          <ResponsiveContainer width="100%" height={260}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#f1f5f9" />
              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10, fill: '#94a3b8' }} />
              <Radar name="Activity" dataKey="value" stroke="#C8102E" fill="#C8102E" fillOpacity={0.15} strokeWidth={2} />
              <Tooltip contentStyle={{ background: '#111', border: '1px solid #222', borderRadius: 12, fontSize: 12 }} />
            </RadarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* ── Recent prompts ────────────────────────────────────────────────── */}
      {!loading && ai.aiPrompts?.recentPrompts?.length > 0 && (
        <Card className="p-6">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Recent AI Prompts</h3>
          <div className="space-y-2">
            {ai.aiPrompts.recentPrompts.map((p, i) => (
              <div key={p.id || i} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                <div className="w-6 h-6 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Brain size={12} className="text-red-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-700 dark:text-gray-300 truncate">{p.question || 'No content'}</p>
                  <div className="flex items-center gap-3 mt-1">
                    {p.category && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-full font-medium">{p.category}</span>
                    )}
                    {p.createdAt && (
                      <span className="text-[10px] text-gray-400">{new Date(p.createdAt).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {!loading && Object.keys(ai).length === 0 && (
        <EmptyState icon={Brain} title="No AI data for this period" sub="AI analytics will appear once users interact with AI features" />
      )}
    </div>
  );
}